import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getApiData as getCompanyDataApi } from 'services/CompanyService'
import { getApiData as getRolesDataApi } from 'services/RolesService'
import { getApiData as getCustomersDataApi } from 'services/CustomersService'
import { getApiData as getIntegrationTypeDataApi } from 'services/IntegrationTypeService'
import { getApiData as getIntegrationDataApi } from 'services/IntegrationService'
import { getApiListData as getProductDataApi } from 'services/ProductsService'
import { getApiData as getApiWarehouseData } from 'services/WarehouseService'
import { getApiData as getApiOrderTypesData } from 'services/OrderTypeService'
import { actionType, moduleName, title } from 'components/helper'
import { getApiPermissionData } from 'services/PermissionService'
import { moduleList } from 'utils/options'
import { getApiOrderStatusData } from 'services/ShipmentsService'
import { getApiAsnTypeData } from 'services/InboundDeliveryService'
import { getApiAsnStatusData } from 'services/AsnService'
import { getApiOrderTypeData } from 'services/SalesOrdersService'

export const getCompanyData = createAsyncThunk('listdata/getCompanyData', async () => {
    const response = await getCompanyDataApi()
    const mappedData = response.data?.results
        ?.map(item => {
            item.label = item.name
            item.value = item.id
            return item
        })
        .sort((a, b) => a.code.localeCompare(b.code))
    return mappedData
})

export const getRolesData = createAsyncThunk('listdata/getRolesData', async () => {
    const response = await getRolesDataApi()
    return response.data?.results?.map(item => {
        item.label = item.name
        item.value = item.id
        return item
    })
})

export const getCustomersData = createAsyncThunk('listdata/getCustomersData', async () => {
    const response = await getCustomersDataApi()
    const mappedData = response.data?.results
        ?.map(item => {
            item.label = item.customer_code
            item.value = item.id
            return item
        })
        .sort((a, b) => a.customer_code.localeCompare(b.customer_code))
    return mappedData
})

export const getWarehouseData = createAsyncThunk('listdata/getWarehouseData', async () => {
    const response = await getApiWarehouseData()
    return response.data?.results?.map(item => {
        item.label = item?.warehouse_code
        item.value = item?.warehouse_code
        return item
    })
})

export const getIntegrationTypeData = createAsyncThunk('listdata/getIntegrationTypeData', async () => {
    const response = await getIntegrationTypeDataApi()
    return response.data?.results?.map(item => {
        item.label = item.name
        item.value = item.id
        return item
    })
})

export const getProductData = createAsyncThunk('listdata/getProductData', async () => {
    const response = await getProductDataApi()
    return response.data?.results?.map(item => {
        item.label = item.sku
        item.value = item.sku
        return item
    })
})

export const getIntegrationData = createAsyncThunk('listdata/getIntegrationData', async () => {
    const response = await getIntegrationDataApi()
    return response.data?.results?.map(item => {
        item.label = `${item.name}-${item.customer_code}`
        item.value = item.id
        return item
    })
})

export const getOrderTypeData = createAsyncThunk('listdata/getOrderTypeData', async () => {
    const response = await getApiOrderTypesData()
    return response.data?.results?.map(item => {
        item.label = item.order_type_name
        item.value = item.order_type_name
        return item
    })
})

export const getOrderTypeFilterData = createAsyncThunk('listdata/getOrderTypeFilterData', async () => {
    const response = await getApiOrderTypeData()
    return response.data?.map(item => {
        return { label: item, value: item }
    })
})

export const getAsnTypeFilterData = createAsyncThunk('listdata/getAsnTypeFilterData', async () => {
    const response = await getApiAsnTypeData()
    return response.data?.map(item => {
        return { label: item, value: item }
    })
})

export const getWmsOrderStatusFilterData = createAsyncThunk('listdata/getWmsOrderStatusFilterData', async () => {
    const response = await getApiOrderStatusData()
    return response.data?.map(item => {
        return { label: item, value: item }
    })
})

export const getWmsAsnStatusFilterData = createAsyncThunk('listdata/getWmsAsnStatusFilterData', async () => {
    const response = await getApiAsnStatusData()
    return response.data?.map(item => {
        return { label: item, value: item }
    })
})

export const getPermissionData = createAsyncThunk('listdata/getPermissionData', async () => {
    const response = await getApiPermissionData()
    const moduleData = response.data.results
        // .filter(item => !item.name.includes('delete'))
        .filter(item => {
            let name = item.name.split(' ')
            name.splice(0, 2)
            name = name.join(' ')
            return moduleList.find(value => value.key === name)
        })
        .map(item => {
            let nameArr = item.name.split(' ')
            nameArr.splice(0, 2)
            nameArr = nameArr.join(' ')
            item.module = moduleName(nameArr)
            item.action = actionType(item.name)
            item.title = title(item.name)
            return item
        })
    return Object.groupBy(moduleData, ({ module }) => module)
})

const dataSlice = createSlice({
    name: 'listdata',
    initialState: {
        permissionList: [],
        companyData: [],
        rolesData: [],
        customersData: [],
        warehouseData: [],
        integrationTypeData: [],
        integrationData: [],
        productData: [],
        orderTypeFilterData: [],
        asnTypeFilterData: [],
        wmsOrderStatusFilterData: [],
        wmsAsnStatusFilterData: [],
    },
    reducers: {
        setCompanyData: (state, { payload }) => {
            state.companyData = payload
        },
        setRolesData: (state, { payload }) => {
            state.rolesData = payload
        },
        setCustomersData: (state, { payload }) => {
            state.customersData = payload
        },
        setWarehouseData: (state, { payload }) => {
            state.warehouseData = payload
        },
        setPermissionData: (state, { payload }) => {
            state.permissionList = payload
        },
        setIntegrationTypeData: (state, { payload }) => {
            state.integrationTypeData = payload
        },
        setIntegrationData: (state, { payload }) => {
            state.integrationData = payload
        },
        setProductData: (state, { payload }) => {
            state.productData = payload
        },
        setOrderTypeData: (state, { payload }) => {
            state.orderTypeData = payload
        },
    },
    extraReducers: builder => {
        builder
            //company
            .addCase(getCompanyData.pending, state => {
                state.loading = true
            })
            .addCase(getCompanyData.fulfilled, (state, action) => {
                state.loading = false
                state.companyData = action.payload
            })
            .addCase(getCompanyData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //roles
            .addCase(getRolesData.pending, state => {
                state.loading = true
            })
            .addCase(getRolesData.fulfilled, (state, action) => {
                state.loading = false
                state.rolesData = action.payload
            })
            .addCase(getRolesData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //customers
            .addCase(getCustomersData.pending, state => {
                state.loading = true
            })
            .addCase(getCustomersData.fulfilled, (state, action) => {
                state.loading = false
                state.customersData = action.payload
            })
            .addCase(getCustomersData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //warehouse
            .addCase(getWarehouseData.pending, state => {
                state.loading = true
            })
            .addCase(getWarehouseData.fulfilled, (state, action) => {
                state.loading = false
                state.warehouseData = action.payload
            })
            .addCase(getWarehouseData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //permission data
            .addCase(getPermissionData.pending, state => {
                state.loading = true
            })
            .addCase(getPermissionData.fulfilled, (state, action) => {
                state.loading = false
                state.permissionList = action.payload
            })
            .addCase(getPermissionData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //integration type data
            .addCase(getIntegrationTypeData.pending, state => {
                state.loading = true
            })
            .addCase(getIntegrationTypeData.fulfilled, (state, action) => {
                state.loading = false
                state.integrationTypeData = action.payload
            })
            .addCase(getIntegrationTypeData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //integration data
            .addCase(getIntegrationData.pending, state => {
                state.loading = true
            })
            .addCase(getIntegrationData.fulfilled, (state, action) => {
                state.loading = false
                state.integrationData = action.payload
            })
            .addCase(getIntegrationData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //product data
            .addCase(getProductData.pending, state => {
                state.loading = true
            })
            .addCase(getProductData.fulfilled, (state, action) => {
                state.loading = false
                state.productData = action.payload
            })
            .addCase(getProductData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //order-type data
            .addCase(getOrderTypeData.pending, state => {
                state.loading = true
            })
            .addCase(getOrderTypeData.fulfilled, (state, action) => {
                state.loading = false
                state.orderTypeData = action.payload
            })
            .addCase(getOrderTypeData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //order-type-filter data
            .addCase(getOrderTypeFilterData.pending, state => {
                state.loading = true
            })
            .addCase(getOrderTypeFilterData.fulfilled, (state, action) => {
                state.loading = false
                state.orderTypeFilterData = action.payload
            })
            .addCase(getOrderTypeFilterData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //asn-type-filter data
            .addCase(getAsnTypeFilterData.pending, state => {
                state.loading = true
            })
            .addCase(getAsnTypeFilterData.fulfilled, (state, action) => {
                state.loading = false
                state.asnTypeFilterData = action.payload
            })
            .addCase(getAsnTypeFilterData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //order-status-filter data
            .addCase(getWmsOrderStatusFilterData.pending, state => {
                state.loading = true
            })
            .addCase(getWmsOrderStatusFilterData.fulfilled, (state, action) => {
                state.loading = false
                state.wmsOrderStatusFilterData = action.payload
            })
            .addCase(getWmsOrderStatusFilterData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //asn-status-filter data
            .addCase(getWmsAsnStatusFilterData.pending, state => {
                state.loading = true
            })
            .addCase(getWmsAsnStatusFilterData.fulfilled, (state, action) => {
                state.loading = false
                state.wmsAsnStatusFilterData = action.payload
            })
            .addCase(getWmsAsnStatusFilterData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
    },
})

export const {
    setCompanyData,
    setRolesData,
    setCustomersData,
    setWarehouseData,
    setPermissionData,
    setIntegrationTypeData,
    setIntegrationData,
    setProductData,
    setOrderTypeData,
} = dataSlice.actions

export default dataSlice.reducer
